import { InputLabel, MenuItem, Select } from '@mui/material';
import Box from '@mui/material/Box';
import { Formik } from 'formik';
import { observer } from 'mobx-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { LoginInput, LoginButton } from 'src/components/mui-overrides/Login';
import { ErrorMessage } from 'src/components/ui/ErrorMessage';
import { Colors } from 'src/components/utils/Theme';
import { Routes } from 'src/router/Routes';
import { authStore } from 'src/stores/AuthStore';
import { generalStore } from 'src/stores/GeneralStore';
import { object, string } from 'yup';

const AuthLoginSite: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const validationSchema = object({
    email: string().email(t('login.email.validation.invalid')).required(t('login.email.validation.required')).trim(),
    password: string().min(8, t('login.password.validation.min')).required(t('login.password.validation.required')),
    platform: string().default('brickwise'),
  });
  const [showError, setShowError] = useState(false);

  const initialValues = {
    email: '',
    password: '',
    platformProvider: 'brickwise',
  };

  const handleSubmit = async (values: { email: string; password: string; platformProvider: string }) => {
    generalStore.setIsLoading(true);
    setShowError(false);
    try {
      await authStore.login(values.email, values.password, values.platformProvider);
      if (authStore.error) {
        setShowError(true);
      } else {
        navigate(Routes.ASSETS.ROOT);
      }
    } catch (error) {
      generalStore.setError('Unknown error');
    }

    generalStore.setIsLoading(false);
  };

  return (
    <div
      style={{
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: showError ? '5px solid ' + Colors.SECONDARY_COLOR : '',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexGlow: 1,
          maxWidth: 350,
        }}
      >
        <img style={{ width: 170, marginBottom: 25 }} src="/logo.svg" alt="brickwise-logo" />

        <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              <LoginInput
                margin="normal"
                fullWidth
                id="email"
                label={t('login.email.label')}
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                variant="filled"
                helperText={formik.errors.email && formik.touched.email ? formik.errors.email : ''}
              />
              <LoginInput
                margin="normal"
                fullWidth
                name="password"
                label={t('login.password.label')}
                type="password"
                id="password"
                variant="filled"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.errors.password && formik.touched.password ? formik.errors.password : ''}
              />
              <InputLabel id="demo-simple-select-label">Platform Provider</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={formik.values.platformProvider}
                label="platform"
                style={{ width: '100%', marginBottom: 16 }}
                onChange={(value) => {
                  // manually changing the value
                  formik.setFieldValue('platformProvider', value.target.value);
                }}
              >
                <MenuItem value={'brickwise'}>Brickwise</MenuItem>
                <MenuItem value={'realpart'}>RealPart</MenuItem>
                <MenuItem value={'coppen'}>Coppen</MenuItem>
                <MenuItem value={'prokon'}>Prokon</MenuItem>
              </Select>
              <LoginButton size="large" type="submit" fullWidth variant="contained" style={{ marginTop: 16 }}>
                {t('login.button.label')}
              </LoginButton>
              <div style={{ paddingTop: 24, textAlign: 'center' }}>
                <Link style={{ color: 'black' }} to={Routes.FORGOT_PASSWORD}>
                  {t('login.forgotPassword')}
                </Link>
              </div>
            </form>
          )}
        </Formik>
        <ErrorMessage
          style={{
            visibility: showError ? 'visible' : 'hidden',
          }}
          message={t('login.error')}
        />
      </Box>
    </div>
  );
};

export default observer(AuthLoginSite);
